import { Storage } from '@/utils/storage';

const CACHE_KEY_PREFIX = 'storage_key_';

/**
 * @description 设置缓存
 * @function setStorage
 * @param {string} key 缓存键名
 * @param {any} data 缓存数据
 */
export function setStorage(key, data) {
  try {
    Storage.setItem(`${CACHE_KEY_PREFIX}${key}`, data);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log('setStorage error', error);
  }
}

/**
 * @description 设置缓存（多少小时后过期）
 * @function setStorageExpiresHour
 * @param {string} key 缓存键名
 * @param {any} data 缓存数据
 * @param {number} hour 缓存过期时间（小时）
 */
export function setStorageExpiresHour(key, data, hour = 24) {
  try {
    Storage.setItemExpiresHour(`${CACHE_KEY_PREFIX}${key}`, data, hour);
    return true;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log('setStorageExpiresHour error', error);
    return false;
  }
}

/**
 * @description 获取缓存
 * @function getStorage
 * @param {string} key 缓存键名
 * @returns {any} 缓存数据
 */
export function getStorage(key) {
  try {
    return Storage.getItem(`${CACHE_KEY_PREFIX}${key}`);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log('getStorage error', error);
    return null;
  }
}

/**
 * @description 删除缓存
 * @function removeStorage
 * @param {string} key 缓存键名
 */
export function removeStorage(key) {
  try {
    Storage.removeItem(`${CACHE_KEY_PREFIX}${key}`);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log('removeStorage error', error);
  }
}

/**
 * @description 接口请求缓存方法（先读取缓存展示，然后请求最新数据更新缓存）
 * @function requestWithCache
 * @param {Object} options - 配置参数
 * @param {Function} options.request - 请求函数，返回Promise
 * @param {string} options.cacheKey - 缓存键名
 * @param {number} [options.expireHours] - 缓存过期时间（小时）
 * @param {Function} [options.onData] - 统一数据回调函数
 *   - 参数: {type: 'cache'|'fresh'|'error', data: any, error?: Error, hadCache?: boolean}
 * @param {boolean} [options.skipCache=false] - 是否跳过缓存直接请求
 * @param {Function} [options.validate] - 缓存数据验证函数，返回true表示缓存有效
 * @returns {Promise<{data: any, fromCache: boolean, error?: Error}>} 返回数据和数据来源
 */
export function requestWithCache(options) {
  const {
    request,
    cacheKey,
    expireHours = 24,
    onData,
    skipCache = false,
    validate,
  } = options;

  if (!request || typeof request !== 'function') {
    throw new Error('request参数必须是一个函数');
  }

  if (!cacheKey || typeof cacheKey !== 'string') {
    throw new Error('cacheKey参数必须是字符串');
  }

  return new Promise((resolve) => {
    let hasResolved = false;
    let cacheData = null;
    let hasCacheData = false;

    // 第一步：尝试读取缓存数据
    if (!skipCache) {
      try {
        cacheData = getStorage(cacheKey);

        // 如果有缓存数据且通过验证
        if (cacheData) {
          // 如果提供了验证函数，检查缓存数据是否有效
          if (validate && typeof validate === 'function') {
            if (!validate(cacheData)) {
              // 缓存数据无效，清除缓存
              removeStorage(cacheKey);
              cacheData = null;
            }
          }

          if (cacheData) {
            hasCacheData = true;
            // 立即返回缓存数据
            if (onData && typeof onData === 'function') {
              onData({ type: 'cache', data: cacheData, hadCache: true });
            }

            if (!hasResolved) {
              hasResolved = true;
              resolve({ data: cacheData, fromCache: true });
            }
          }
        }
      } catch (error) {
        // 读取缓存失败时静默处理
      }
    }

    // 第二步：发起网络请求获取最新数据
    request()
      .then((freshData) => {
        // 更新缓存
        setStorageExpiresHour(cacheKey, freshData, expireHours);

        // 如果获取到新数据
        if (onData && typeof onData === 'function') {
          onData({ type: 'fresh', data: freshData, hadCache: hasCacheData });
        }

        // 如果之前没有缓存数据，直接resolve最新数据
        if (!hasResolved) {
          hasResolved = true;
          resolve({ data: freshData, fromCache: false });
        }
      })
      .catch((error) => {
        if (onData && typeof onData === 'function') {
          onData({
            type: 'error',
            data: null,
            error,
            hadCache: hasCacheData,
          });
        }

        // 如果没有缓存数据且请求失败，返回错误
        if (!hasResolved) {
          hasResolved = true;
          resolve({ data: null, fromCache: false, error });
        }
      });
  });
}
